import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { Progress } from '../ui/progress'
import { Separator } from '../ui/separator'
import { Alert, AlertDescription } from '../ui/alert'
import { 
  Brain,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Target,
  Lightbulb,
  Zap,
  RefreshCw,
  ArrowRight,
  Eye,
  BarChart3,
  PieChart,
  Activity
} from 'lucide-react'
import { mockAI } from '../../utils/mockAI'

interface Brand {
  id: string
  name: string
  metadata?: {
    industry?: string
  }
}

interface AIInsightsDashboardProps {
  brand: Brand
  className?: string
  onNavigateToColorPalette?: () => void
  onNavigateToVoiceGuidelines?: () => void
  onNavigateToAssetGeneration?: () => void
}

interface BrandMetric {
  name: string
  value: number
  change: number
  trend: 'up' | 'down' | 'stable'
  status: 'good' | 'warning' | 'critical'
}

export function AIInsightsDashboard({ 
  brand, 
  className = '',
  onNavigateToColorPalette,
  onNavigateToVoiceGuidelines,
  onNavigateToAssetGeneration
}: AIInsightsDashboardProps) {
  const [insights, setInsights] = useState<any[]>([])
  const [metrics, setMetrics] = useState<BrandMetric[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    loadInsights()
    loadMetrics()
  }, [brand.id])

  const loadInsights = async () => {
    setIsLoading(true)
    try {
      const aiInsights = await mockAI.getInsights(brand.id)
      setInsights(aiInsights)
      setLastUpdated(new Date())
    } catch (error) {
      console.error('Failed to load insights:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const loadMetrics = async () => {
    // Mock brand performance metrics
    const mockMetrics: BrandMetric[] = [
      {
        name: 'Brand Consistency',
        value: 87,
        change: 5,
        trend: 'up',
        status: 'good'
      },
      {
        name: 'Color Accessibility',
        value: 72,
        change: -3,
        trend: 'down',
        status: 'warning'
      },
      {
        name: 'Typography Hierarchy',
        value: 94,
        change: 8,
        trend: 'up',
        status: 'good'
      },
      {
        name: 'Content Completeness',
        value: 65,
        change: 12,
        trend: 'up',
        status: 'warning'
      }
    ]
    setMetrics(mockMetrics)
  }

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'performance': return <BarChart3 className="w-4 h-4 text-blue-500" />
      case 'opportunity': return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-500" />
      case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />
      default: return <Lightbulb className="w-4 h-4 text-purple-500" />
    }
  }

  const getMetricTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="w-4 h-4 text-green-500" />
      case 'down': return <TrendingDown className="w-4 h-4 text-red-500" />
      default: return <Activity className="w-4 h-4 text-slate-400" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'good': return 'text-green-600 bg-green-50 border-green-200'
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'critical': return 'text-red-600 bg-red-50 border-red-200'
      default: return 'text-slate-600 bg-slate-50 border-slate-200'
    }
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Brain className="w-5 h-5" />
            AI Brand Intelligence
          </h3>
          <p className="text-sm text-slate-600">
            Real-time insights and recommendations for {brand.name}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-xs text-slate-500">
              Updated {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadInsights}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {metrics.map((metric, index) => (
          <Card key={index} className={`border ${getStatusColor(metric.status)}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getMetricTrendIcon(metric.trend)}
                  <span className="text-sm font-medium">{metric.name}</span>
                </div>
                <div className="flex items-center gap-1 text-xs">
                  <span className={metric.change >= 0 ? 'text-green-600' : 'text-red-600'}>
                    {metric.change >= 0 ? '+' : ''}{metric.change}%
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold">{metric.value}%</span>
                </div>
                <Progress value={metric.value} className="h-2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* AI Insights */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              Priority Insights
            </CardTitle>
            <CardDescription>
              AI-identified areas needing immediate attention
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded w-full"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {insights.filter(insight => insight.priority === 'high').map((insight, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                    {getInsightIcon(insight.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-medium text-sm">{insight.title}</h4>
                        {insight.metric && (
                          <Badge variant="outline" className="text-xs">{insight.metric}%</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{insight.description}</p>
                      {insight.recommendation && (
                        <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                          <strong>Action:</strong> {insight.recommendation}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                
                {insights.filter(insight => insight.priority === 'high').length === 0 && (
                  <div className="text-center py-6 text-slate-500">
                    <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                    <p className="text-sm">No high-priority issues detected!</p>
                    <p className="text-xs">Your brand is performing well.</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Growth Opportunities
            </CardTitle>
            <CardDescription>
              AI-suggested improvements to enhance your brand
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {insights.filter(insight => insight.type === 'opportunity').map((insight, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-sm">{insight.title}</h4>
                    <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                      Opportunity
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">{insight.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">
                      Potential impact: {insight.metric ? `+${insight.metric}%` : 'High'}
                    </span>
                    <Button size="sm" variant="outline">
                      <Eye className="w-3 h-3 mr-1" />
                      Explore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Recommended Actions
          </CardTitle>
          <CardDescription>
            Quick wins to improve your brand performance today
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-start hover:bg-blue-50 hover:border-blue-200 transition-colors"
              onClick={onNavigateToColorPalette}
            >
              <div className="flex items-center gap-2 mb-2">
                <PieChart className="w-4 h-4" />
                <span className="font-medium">Review Color Palette</span>
              </div>
              <p className="text-xs text-slate-600 text-left">
                Improve accessibility scores by 15%
              </p>
              <ArrowRight className="w-4 h-4 mt-2 self-end" />
            </Button>

            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-start hover:bg-green-50 hover:border-green-200 transition-colors"
              onClick={onNavigateToVoiceGuidelines}
            >
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4" />
                <span className="font-medium">Update Voice Guidelines</span>
              </div>
              <p className="text-xs text-slate-600 text-left">
                Strengthen brand consistency across content
              </p>
              <ArrowRight className="w-4 h-4 mt-2 self-end" />
            </Button>

            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-start hover:bg-purple-50 hover:border-purple-200 transition-colors"
              onClick={onNavigateToAssetGeneration}
            >
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4" />
                <span className="font-medium">Generate Assets</span>
              </div>
              <p className="text-xs text-slate-600 text-left">
                Create social media templates with AI
              </p>
              <ArrowRight className="w-4 h-4 mt-2 self-end" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* AI Confidence Score */}
      <Alert>
        <Brain className="w-4 h-4" />
        <AlertDescription>
          <div className="flex items-center justify-between">
            <span>
              <strong>AI Confidence:</strong> These insights are generated with 
              {' '}<span className="font-semibold text-blue-600">87% confidence</span>{' '}
              based on your current brand data and industry benchmarks.
            </span>
            <Button size="sm" variant="outline">
              Learn More
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  )
}